import { Box, Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import { Button, ControlledTextField2 } from 'components/UI';
import React, { Fragment, FunctionComponent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { wbp } from 'Theme';
import { FieldError } from '@spike/model';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { Prospect } from 'model/ProspectMessage';
import { v4 as uuid } from 'uuid';
import { createEmptyClient } from '@spike/client-model';
import { set } from 'lodash';
import ControlledPhoneField3 from 'components/UI/ControlledPhoneField3';
import { validateClient } from 'components/Client/Validations';
import { ClientsStatus, saveClientThunk } from '@spike/clients-action';
import { RootState } from 'store';
import { fetchProspectConversations } from 'actions/prospectMessage/prospectMessageActions';
import { MessageFilterTypes } from '../../Model';
import { ProspectMessageState } from 'reducers/prospectMessage/prospectMessageState';
import { showSuccess } from '@spike/notifications-action';

interface AddNewClientProps {
    prospect: Prospect;
    className?: string;
    onClose?: () => void;
    onSaveNewClient: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: '100%',
            height: '100%',
            display: 'flex',
            overflowY: 'auto',
            flexDirection: 'column',
            backgroundColor: '#ffffff',

            [theme.breakpoints.up('md')]: {
                width: '486px',
                height: '581px',
                borderRadius: 27
            }
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            padding: '37px 37px 0px 37px'
        },
        headerTitle: {
            fontSize: 20,
            lineHeight: 1,
            fontWeight: 600,
            textAlign: 'center',

            [theme.breakpoints.up('md')]: {
                fontSize: 22
            }
        },
        closeIcon: {
            fontSize: 22,
            cursor: 'pointer',

            [theme.breakpoints.up('md')]: {
                color: '#ffffff'
            }
        },
        body: {
            display: 'flex',
            flexGrow: 1,
            overflowY: 'auto',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
            padding: '16px 16px 4px',

            [theme.breakpoints.up('md')]: {
                padding: '17px 37px 17px 37px'
            }
        },
        bodyHeader: {
            display: 'flex',
            flexWrap: 'wrap',
            marginBottom: 24,

            [theme.breakpoints.up('md')]: {
                marginBottom: 32
            }
        },
        bodyHeaderLeft: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',

            [theme.breakpoints.down('sm')]: {
                marginBottom: 16,
                paddingBottom: 16,
                borderBottom: 'solid 1px #D4D4D4'
            },
            [theme.breakpoints.up('md')]: {
                width: '70%'
            }
        },
        bodyHeaderRight: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',

            [theme.breakpoints.up('md')]: {
                width: '30%',
                alignItems: 'flex-end'
            }
        },
        name: {
            fontSize: 16,
            lineHeight: 1,
            fontWeight: 600,
            color: '#000000',
            marginBottom: 8,

            [theme.breakpoints.up('md')]: {
                fontSize: 18
            }
        },
        email: {
            fontSize: 16,
            lineHeight: 1,
            fontWeight: 400,
            color: '#000000',

            [theme.breakpoints.up('md')]: {
                fontSize: 18
            }
        },
        title: {
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 400,
            color: '#8E8E8E',
            marginBottom: 8,
            letterSpacing: '1.12px',
            textTransform: 'uppercase'
        },
        date: {
            fontSize: 16,
            lineHeight: 1,
            fontWeight: 400,
            color: '#000000',

            [theme.breakpoints.up('md')]: {
                fontSize: 18
            }
        },
        reason: {
            '& textarea': {
                [theme.breakpoints.down(wbp)]: {
                    height: '60px !important'
                },
                [theme.breakpoints.up(wbp)]: {
                    height: '80px !important'
                }
            }
        },
        footer: {
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white',
            padding: '0px 16px 24px',

            [theme.breakpoints.up('md')]: {
                borderTop: '#D4D4D4 1px solid',
                marginTop: 24,
                padding: '0px 32px 24px'
            }
        },
        footerRow: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            justifyContent: 'space-evenly',
            marginTop: 16
        },

        spinner: {
            [theme.breakpoints.down(wbp)]: {
                marginTop: '40px'
            },
            [theme.breakpoints.up(wbp)]: {
                marginTop: '50px'
            }
        },
        amountField: {
            'width': '100%',

            [theme.breakpoints.up('md')]: {
                width: '180px'
            },

            '& .MuiInputBase-root': {
                height: 54,

                [theme.breakpoints.up('md')]: {
                    height: 47
                }
            }
        },
        button: {
            'height': 47,
            'width': 191,
            '& span': {
                fontSize: 16
            },

            [theme.breakpoints.down('sm')]: {
                'height': 55,
                'marginTop': 32,

                '& span': {
                    fontSize: 18
                }
            }
        },
        inputLabel: {
            '& p': {
                fontSize: 14,
                fontWeight: 400
            }
        }
    })
);

export const AddNewClient: FunctionComponent<AddNewClientProps> = props => {
    const classes = useStyles();
    const marketplace = useMarketplace();
    const initialClient = { ...createEmptyClient(uuid), phone: props.prospect.phone.slice(-10) };

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const [loading, setLoading] = useState(false);
    const clientsStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);
    const prospectMessagesState = useSelector<RootState, ProspectMessageState>(state => state.prospectMessages);

    const [errors, setErrors] = useState<Array<FieldError>>([]);
    const [client, setclient] = useState(initialClient);

    useNonInitialEffect(() => {
        switch (clientsStatus) {
            case ClientsStatus.SaveSuccess: {
                setLoading(false);
                dispatch(
                    fetchProspectConversations(
                        prospectMessagesState.metaProspectConversations?.page || 1,
                        prospectMessagesState.metaProspectConversations?.size || 30,
                        prospectMessagesState.filter || MessageFilterTypes.NEWEST,
                        prospectMessagesState.query || ''
                    )
                );
                dispatch(showSuccess('Client saved successfully'));
                props.onSaveNewClient && props.onSaveNewClient();
                props.onClose && props.onClose();
                break;
            }
            case ClientsStatus.Error: {
                setLoading(false);
                break;
            }
        }
    }, [clientsStatus]);

    const changeValueHandler = (value: any, fieldName?: string) => {
        if (fieldName) {
            const updatedClient = { ...client };
            set(updatedClient, fieldName, value);
            setclient(updatedClient);
        }
    };

    const handleSave = () => {
        const errors = validateClient(client, marketplace.basics.address.country!);
        setErrors(errors);

        if (errors.length === 0) {
            dispatch(saveClientThunk(apiClientWrapper, { ...client }, false));
            setLoading(true);
        }
    };

    return (
        <Box className={clsx(classes.container, props.className)}>
            <Box className={classes.header}>
                <Typography className={classes.headerTitle}>Add New Client</Typography>
            </Box>
            <Box className={classes.body}>
                <>
                    <Grid
                        container
                        item
                        spacing={4}
                    >
                        <Grid
                            item
                            xs={12}
                            md={12}
                        >
                            <ControlledTextField2
                                disabled={loading}
                                labelClassName={classes.inputLabel}
                                placeholder="First Name"
                                label="First Name"
                                name="firstName"
                                id="clients_input_firstname"
                                value={client.firstName}
                                onChange={changeValueHandler}
                                autoComplete={'off'}
                                errors={errors}
                                required={true}
                                capitalize={true}
                                maxLength={30}
                                allowSpecialCharacters={true}
                                withEmojis={true}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={12}
                        >
                            <ControlledTextField2
                                disabled={loading}
                                labelClassName={classes.inputLabel}
                                placeholder="Last Name"
                                label="Last Name"
                                name="lastName"
                                id="clients_input_lastname"
                                value={client.lastName}
                                autoComplete={'off'}
                                errors={errors}
                                onChange={changeValueHandler}
                                required={true}
                                capitalize={true}
                                maxLength={30}
                                allowSpecialCharacters={true}
                                withEmojis={true}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={12}
                        >
                            <ControlledPhoneField3
                                disabled={loading}
                                labelClassName={classes.inputLabel}
                                label="Phone"
                                placeholder="(___) ___-____"
                                name="phone"
                                autoComplete={'off'}
                                id="clients_input_phone"
                                errors={errors}
                                value={client.phone}
                                onChange={changeValueHandler}
                                required={true}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={12}
                        >
                            <ControlledTextField2
                                disabled={loading}
                                labelClassName={classes.inputLabel}
                                label="Email"
                                placeholder="Email"
                                name="email"
                                id="clients_input_email"
                                value={client.email}
                                onChange={changeValueHandler}
                                autoComplete={'off'}
                                errors={errors}
                                required={true}
                            />
                        </Grid>
                    </Grid>
                </>
            </Box>

            <Box className={classes.footer}>
                {!loading && (
                    <Fragment>
                        <Box className={classes.footerRow}>
                            <Button
                                label="Discard"
                                onClick={props.onClose}
                                color="black"
                                size="large"
                                className={classes.button}
                            />
                            <Button
                                label="Save"
                                onClick={handleSave}
                                loading={loading}
                                color="green"
                                size="large"
                                className={classes.button}
                            />
                        </Box>
                    </Fragment>
                )}
            </Box>
        </Box>
    );
};

export default AddNewClient;
